import type { Event, EventHint } from "@sentry/core";

// Must match the message produced in apps/nextjs/src/components/Error.tsx
// (`Unexpected authenticated ${statusLabel}`); a rename there silently disables fingerprinting.
const AUTHENTICATED_ERROR_PREFIX = "Unexpected authenticated";

// Statuses that represent a resource-level authorization/conflict/validation problem, where
// splitting by navigation path is meaningful. Systemic statuses (429 rate-limit, 503 outage)
// are intentionally excluded so a spike stays one issue instead of fragmenting across routes.
const ROUTE_SPLIT_STATUSES = new Set(["403", "409", "412", "422"]);

// Collapse dynamic URL segments (cuid or numeric record IDs like 108434) to ":id" so navigation
// paths group by route shape, not by record. A segment is treated as an ID when it contains a
// digit and is not a versioned route suffix (…v0, …v2). The first path segment is never collapsed,
// so top-level numeric pages (the /404 page) stay intact while nested record IDs
// (/dashboard/bids/108434) normalize to :id. Static segments (dashboard) and word-slug params
// (tickets/mine) have no digit and are left untouched.
function normalizeRoutePath(rawUrl: string): string {
  const path = (rawUrl.split("?")[0] ?? "").split("#")[0] ?? "";
  return path
    .split("/")
    .map((segment, index) => (index > 1 && /\d/.test(segment) && !/v\d+$/i.test(segment) ? ":id" : segment))
    .join("/");
}

/**
 * beforeSend hook that splits the "Unexpected authenticated <status>" issue (raised by the
 * apps/nextjs Error page) by navigation path. That error carries a single message and
 * stacktrace, so Sentry's default grouping collapses every authenticated 4xx across all
 * routes into one issue.
 *
 * Resource-level statuses (see ROUTE_SPLIT_STATUSES) are fingerprinted by HTTP status, the
 * referring route (from the last navigation breadcrumb, which carries the raw URL) and the
 * destination route (event.transaction). Both routes are run through normalizeRoutePath —
 * transaction is normally already param-free, but normalizing it guards against the cases
 * where Sentry populates it with a concrete URL. Systemic statuses (429/503) are grouped by
 * status alone so an outage stays a single issue. Always exposes the referrer as a filterable
 * `from.route` tag.
 */
export function fingerprintAuthenticatedError<T extends Event>(event: T, _hint: EventHint): T {
  try {
    const exceptionValue = event.exception?.values?.[0]?.value;
    if (!exceptionValue?.startsWith(AUTHENTICATED_ERROR_PREFIX)) {
      return event;
    }

    const toRoute = event.transaction ? normalizeRoutePath(event.transaction) : "unknown";
    const lastNavigation = event.breadcrumbs?.filter((breadcrumb) => breadcrumb.category === "navigation").pop();
    const rawFrom: unknown = lastNavigation?.data?.from;
    const fromRoute = typeof rawFrom === "string" ? normalizeRoutePath(rawFrom) : "direct";
    const status = String(event.tags?.["http.status"] ?? "unknown");

    event.fingerprint = ROUTE_SPLIT_STATUSES.has(status)
      ? ["authenticated-http-error", status, fromRoute, toRoute]
      : ["authenticated-http-error", status];
    event.tags = { ...event.tags, "from.route": fromRoute };
  } catch {
    // Don't break Sentry error reporting if fingerprinting fails
  }

  return event;
}
